import { useEffect, useRef, useState } from "react";
import { renameUnknownFood, searchFoodsRemotely, type FoodSearchResult, type ParsedVoiceMealDraft } from "../../application/remote-meal-parser";
import type { UnknownFoodTarget } from "../../application/unknown-food";
import type { VoiceTranscriptionSession } from "../../application/voice-transcription";
import { mergeVoiceTranscript } from "../../application/voice-transcript";
import { webSpeechTranscription } from "../../voice/web-speech-transcription";

export function UnknownFoodDialog({ draft, target, remaining, onChange, onDismiss }: { draft: ParsedVoiceMealDraft; target: UnknownFoodTarget; remaining: number; onChange: (draft: ParsedVoiceMealDraft) => void; onDismiss: () => void }) {
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState<FoodSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const [listening, setListening] = useState(false);
  const [voicePending, setVoicePending] = useState(false);
  const voiceBusy = listening || voicePending;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const session = useRef<VoiceTranscriptionSession | null>(null);
  const transcript = useRef("");
  const voiceFailed = useRef(false);
  const submitting = useRef(false);
  const alive = useRef(true);
  const request = useRef(0);
  const dialog = useRef<HTMLElement>(null);
  useEffect(() => {
    alive.current = true;
    const focus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    dialog.current?.focus();
    return () => { alive.current = false; request.current++; session.current?.abort(); focus?.focus(); };
  }, []);
  useEffect(() => {
    const query = value.trim();
    if (voiceBusy || busy || query.length < 3) {
      setSuggestions([]);
      setSearching(false);
      setSearchError(false);
      setActiveSuggestion(-1);
      return;
    }
    let current = true;
    setSuggestions([]);
    setActiveSuggestion(-1);
    setSearching(true);
    setSearchError(false);
    const timer = window.setTimeout(() => {
      void searchFoodsRemotely(query).then(results => {
        if (!current) return;
        setSuggestions(results.filter(food => !food.isUserFood).slice(0, 15));
        setActiveSuggestion(-1);
      }).catch(() => {
        if (current) { setSuggestions([]); setSearchError(true); }
      }).finally(() => { if (current) setSearching(false); });
    }, 250);
    return () => { current = false; window.clearTimeout(timer); };
  }, [busy, voiceBusy, value]);
  const submit = async (answer: string) => {
    if (!answer.trim() || submitting.current) return;
    submitting.current = true;
    const id = ++request.current;
    setBusy(true); setError("");
    try {
      const updated = await renameUnknownFood(draft, target, answer.trim());
      if (alive.current && id === request.current) onChange(updated);
    } catch (problem) { if (alive.current && id === request.current) setError(problem instanceof Error ? problem.message : "Не удалось найти продукт."); }
    finally { submitting.current = false; if (alive.current && id === request.current) setBusy(false); }
  };
  const record = () => {
    if (session.current) { session.current.stop(); return; }
    setError(""); setValue(""); transcript.current = ""; voiceFailed.current = false; setVoicePending(true);
    session.current = webSpeechTranscription.start("ru-RU", {
      onListening: () => { if (alive.current) { setVoicePending(false); setListening(true); } },
      onCaptureEnd: () => { if (alive.current) { setListening(false); setVoicePending(true); } },
      onInterim: text => { if (alive.current) setValue(mergeVoiceTranscript(transcript.current, text)); },
      onFinal: text => { transcript.current = mergeVoiceTranscript(transcript.current, text); if (alive.current) setValue(transcript.current); },
      onError: () => { session.current = null; voiceFailed.current = true; if (alive.current) { setVoicePending(false); setListening(false); setError("Не получилось записать голос. Попробуйте ещё раз."); } },
      onEnd: () => { session.current = null; if (!alive.current) return; setVoicePending(false); setListening(false); if (!voiceFailed.current && transcript.current.trim()) void submit(transcript.current); else if (!voiceFailed.current) setError("Не расслышал название. Попробуйте ещё раз."); },
    });
  };
  const selectSuggestion = (food: FoodSearchResult) => {
    const name = food.nutrition?.name || food.matchedName || food.name;
    setSuggestions([]);
    void submit(name);
  };
  return <div className="unknown-food-backdrop" onPointerDown={event => event.stopPropagation()}>
    <section ref={dialog} tabIndex={-1} className="unknown-food-dialog" role="dialog" aria-modal="true" aria-labelledby="unknown-food-title" aria-describedby="unknown-food-description" onKeyDown={event => {
      if (event.key === "Escape") { event.stopPropagation(); onDismiss(); }
      if (event.key === "Tab") {
        const controls = [...event.currentTarget.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled)')];
        const first = controls[0], last = controls.at(-1);
        if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog.current)) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    }}>
      <header><h2 id="unknown-food-title">Продукт не найден</h2><button type="button" aria-label="Закрыть уточнение" onClick={onDismiss}>×</button></header>
      <strong className="unknown-food-name">{target.name}</strong>
      {target.dishName !== target.name && <p className="unknown-food-context">В составе блюда «{target.dishName}»</p>}
      <p id="unknown-food-description">Назовите продукт по-другому или укажите его синоним.</p>
      {remaining > 1 && <p className="unknown-food-context">Нужно уточнить продуктов: {remaining}. Разберём их по очереди.</p>}
      <button type="button" className="unknown-food-primary" disabled={busy || voicePending} onClick={record}>{voicePending ? "Подготавливаю…" : busy ? "Ищу продукт…" : listening ? "Закончить запись" : "Назвать по-другому"}</button>
      {listening && <p role="status">Слушаю… Назовите только продукт.</p>}
      {listening && value && <p aria-live="polite">{value}</p>}
      <div className="unknown-food-manual">
        <label htmlFor="unknown-food-manual-name">Или впишите название</label>
        <input id="unknown-food-manual-name" type="text" role="combobox" aria-autocomplete="list" aria-controls="unknown-food-suggestions" aria-expanded={value.trim().length >= 3 && !voiceBusy && !busy} aria-activedescendant={activeSuggestion >= 0 ? `unknown-food-option-${activeSuggestion}` : undefined} value={value} onChange={event => setValue(event.target.value)} onKeyDown={event => {
          if (event.key === "ArrowDown" && suggestions.length) { event.preventDefault(); setActiveSuggestion(index => (index + 1) % suggestions.length); }
          else if (event.key === "ArrowUp" && suggestions.length) { event.preventDefault(); setActiveSuggestion(index => (index <= 0 ? suggestions.length - 1 : index - 1)); }
          else if (event.key === "Enter" && activeSuggestion >= 0) { event.preventDefault(); selectSuggestion(suggestions[activeSuggestion]!); }
          else if (event.key === "Escape") setSuggestions([]);
        }} placeholder="Введите от 3 букв" autoComplete="off" disabled={busy || voiceBusy} />
        {value.trim().length >= 3 && (searching || suggestions.length > 0 || searchError || (!searching && !suggestions.length)) && <div id="unknown-food-suggestions" className="unknown-food-suggestions" role="listbox" aria-label="Подходящие продукты">
          {searching && <p role="status">Ищу продукты…</p>}
          {searchError && <p role="alert">Не удалось загрузить подсказки.</p>}
          {!searching && !searchError && suggestions.map((food, index) => <button id={`unknown-food-option-${index}`} type="button" role="option" aria-selected={activeSuggestion === index} key={`${food.id}:${food.name}`} disabled={busy} onMouseDown={event => event.preventDefault()} onClick={() => selectSuggestion(food)}><span>{food.matchedName || food.name}</span>{food.attribution && <small>{food.attribution}</small>}</button>)}
          {!searching && !searchError && !suggestions.length && <p>Ничего не найдено. Попробуйте другое название.</p>}
        </div>}
        {suggestions.length > 0 && <small>Выберите продукт из списка. Показано не больше 15 вариантов.</small>}
      </div>
      {error && <p role="alert" className="unknown-food-error">{error}</p>}
      <small>Продукт не найден в базе. Можно назвать его иначе, вписать название или закрыть окно и удалить запись.</small>
    </section>
  </div>;
}
